import {
  ChatMessageGroup,
  ConversationChannelTypeExtended,
  ConversationMessageType,
  TransformedMessage,
} from '@provision/chat-utils';
import {
  ConversationParticipantEntity,
  ConversationParticipantOption,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import {
  ConversationAssigneeGroup,
  ConversationParticipation,
  LabelAssignment,
} from '@provision/entity-types';
import { getFriendlyConversationId } from '@provision/formatting-utils';
import { EMAIL_VALIDATION_REGEX } from 'src/components';
import { Address, mapAddress } from 'src/graphql/entities/address';
import { CONVERSATION_ASSIGNEE_NOT_DEFINED_STR } from 'src/graphql/entities/custom-view';
import { ParticipationBadgeMap } from 'src/pages/Conversations/Chat/utils';
import {
  AddressRaw,
  ClientRaw,
  ConversationParticipantRaw,
  ConversationRaw,
  PartnerRaw,
  PlatformUserRaw,
} from 'src/types';
import { getRawTextFromHTML } from 'src/utils/text-parsing';
import { getAssigneeFullName, getRequesterFullName } from './conversation.mappings.utils';
import { getLatestSummary } from './conversation.summary';
import { Conversation, ConversationParticipant, ConversationType } from './conversation.types';

export function getConversationChannelTypeByParticipation(
  participations: ConversationParticipation[],
): ConversationChannelTypeExtended | null {
  const isEmail = participations.some(
    ({ channel }) => channel?.conversationChannelTypeId === 'email',
  );

  if (isEmail) {
    return ConversationChannelTypeExtended.Email;
  }

  const isChat = participations.some(
    ({ channel }) => channel?.conversationChannelTypeId === 'chat',
  );

  if (isChat) {
    return ConversationChannelTypeExtended.Chat;
  }

  const isPhone = participations.some(
    ({ channel }) => channel?.conversationChannelTypeId === 'phone',
  );

  if (isPhone) {
    return ConversationChannelTypeExtended.Phone;
  }

  return null;
}

export function mapMessagesForChatUI({
  items,
  conversationParticipants,
  badgeMap,
  inactiveUserIds,
  conversationId,
}: {
  conversationId: string;
  items: ChatMessageGroup<ConversationParticipant>[];
  conversationParticipants: {
    engage?: ConversationParticipantRaw[];
    assist?: ConversationParticipantRaw[];
  };
  badgeMap: ParticipationBadgeMap;
  inactiveUserIds: string[];
}): ChatMessageGroup<ConversationParticipant>[] {
  const conversationParticipantsWithEngageModeSet = new Set(
    conversationParticipants.engage?.map((i) => i.participantIdentity) ?? [],
  );

  const conversationParticipantsWithAssistModeSet = new Set(
    conversationParticipants.assist?.map((i) => i.participantIdentity) ?? [],
  );

  const getSingleMessageType = (item: TransformedMessage): ConversationMessageType => {
    if (item.isPrivate) {
      return ConversationMessageType.Private;
    }

    if (conversationParticipantsWithEngageModeSet.has(item.senderId)) {
      return ConversationMessageType.Engage;
    }

    return ConversationMessageType.Assist;
  };

  return items.map((messageGroup) => {
    const shouldSetAssistBadge =
      (messageGroup.sender?.id &&
        conversationParticipantsWithAssistModeSet.has(messageGroup.sender.id)) ||
      messageGroup.id.startsWith('temp-id');

    return {
      ...messageGroup,
      isUserActive:
        messageGroup.senderEntity !== ConversationParticipantEntity.PlatformUser ||
        Boolean(messageGroup.sender?.id && !inactiveUserIds.includes(messageGroup.sender.id)),
      badgeConfig: shouldSetAssistBadge
        ? badgeMap[ConversationParticipationMode.Assist]
        : undefined,
      messages: messageGroup.messages.map((message) => ({
        ...message,
        conversationId,
        type: getSingleMessageType(message),
      })),
    };
  });
}

export const formatConversationRequester = (
  requester?: Conversation['requester'],
): ConversationParticipantOption | null => {
  if (!requester) return null;

  const idAttributes = {
    id: requester.participantIdentity,
    participantIdentity: requester.participantIdentity,
    conversationParticipantId: requester.conversationParticipantId,
  };

  if (requester.client) {
    return {
      ...idAttributes,
      participantEntity: ConversationParticipantEntity.Client,
      firstName: requester.client.firstName,
      lastName: requester.client.lastName,
      email: requester.client.primaryEmail?.value,
      phoneNumber: requester.client.phoneNumbers?.[0]?.value?.toString(),
      entityPartnerId: requester.client.partnerId,
      avatarFileKey: null,
    };
  }

  if (requester.platformUser) {
    return {
      ...idAttributes,
      participantEntity: ConversationParticipantEntity.PlatformUser,
      firstName: requester.platformUser.firstName,
      lastName: requester.platformUser.lastName,
      email: requester.platformUser.email,
      phoneNumber: requester.platformUser.phoneNumber,
      entityPartnerId: requester.platformUser.partnerId,
      avatarFileKey: requester.platformUser.avatarFileKey ?? null,
    };
  }

  if (requester.rsmEmail) {
    return {
      ...idAttributes,
      participantEntity: ConversationParticipantEntity.Rsm,
      firstName: requester.rsmEmail.name,
      lastName: '',
      email: requester.rsmEmail.value,
      phoneNumber: '',
      entityPartnerId: requester.rsmEmail.partnerId,
      avatarFileKey: null,
    };
  }

  const identityIsEmail = requester.participantIdentity?.match(EMAIL_VALIDATION_REGEX);

  return {
    ...idAttributes,
    participantEntity: ConversationParticipantEntity.Unknown,
    firstName: identityIsEmail
      ? requester.participantIdentity
      : CONVERSATION_ASSIGNEE_NOT_DEFINED_STR,
    lastName: '',
    email: identityIsEmail ? requester.participantIdentity : '',
    phoneNumber: '',
    entityPartnerId: requester.partnerId,
    avatarFileKey: null,
  };
};

const filterLabelAssignments = (
  labelAssignments?: LabelAssignment[],
  showPrivateLabels = false,
) => {
  return showPrivateLabels
    ? labelAssignments
    : labelAssignments?.filter((la) => la.label.isPublic);
};

export const NO_SUBJECT_PLACEHOLDER = 'No Subject';

export const formatConversation = (
  conversation: ConversationRaw,
  data?: {
    labelAssignments: LabelAssignment[];
    client?: ClientRaw | null;
    platformUser?: null | PlatformUserRaw;
    assignee?: null | PlatformUserRaw;
    unseenCount?: number;
    activeUserIds?: string[];
    showPrivateLabels: boolean;
    conversationAssigneeGroup?: ConversationAssigneeGroup;
    partner?: PartnerRaw | null;
  },
) => {
  const {
    associatedAddresses,
    conversationId,
    partnerId,
    workspaceConversationId,
    subject,
    requesterId,
    createdAt,
    type,
    conversationUrgencyId,
    conversationAssigneeGroupId,
    conversationAssigneeId,
    conversationStatusId,
    lastMessageReceivedAt,
    conversationStatusUpdatedAt,
    snoozeUntil,
    attachments,
  } = conversation;
  const addresses: Address[] =
    associatedAddresses?.map((address) => mapAddress(address)) ?? [];

  const requester = {
    ...conversation.requester,
    client: data?.client,
    platformUser: data?.platformUser,
    // this assertion is necessary since conversation is actually typed wrongly as it assumes requester will always
    // contain client and platformUser this should be refactored separately
  } as Conversation['requester'];

  const requesterData = formatConversationRequester(requester);
  const partner = conversation.partner || data?.partner || null;
  const latestSummary = getLatestSummary(conversation.summaries);

  return {
    id: conversationId,
    partnerId,
    conversationId,
    workspaceConversationId,
    subject: getRawTextFromHTML(subject) || NO_SUBJECT_PLACEHOLDER,
    requesterId,
    createdAt,
    type: type as ConversationType,
    urgency: conversationUrgencyId,
    partner,
    conversationAssigneeGroupId,
    conversationAssigneeId,
    assignee: data?.assignee,
    requester,
    associatedAddresses: addresses as AddressRaw[],
    address: addresses?.[0] ?? null,
    conversationStatusId,
    lastMessageReceivedAt,
    conversationStatusUpdatedAt,
    snoozeUntil,
    requesterFullName: getRequesterFullName(requesterData, requester),
    assigneeFullName: getAssigneeFullName(data?.assignee, data?.activeUserIds),
    conversationAssigneeGroup: data?.conversationAssigneeGroup,
    attachments: attachments ?? [],
    unseenCount: data?.unseenCount ?? 0,
    friendlyId: getFriendlyConversationId(conversation, conversation.partnerId),
    requesterData,
    summaries: conversation.summaries ?? [],
    latestSummary,
    latestSummaryText: latestSummary?.shortSummary ?? null,
    labelAssignments:
      filterLabelAssignments(data?.labelAssignments, data?.showPrivateLabels) ?? [],
    isDeleted: !!conversation.deletedAt,
  } as Conversation;
};
